import React, { useRef, useState, useEffect } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Upload, ExternalLink, Filter, SlidersHorizontal, Search, X, Check, RotateCcw, BookOpen, FolderTree, Layers, GraduationCap, FileText, HelpCircle, Tag as TagIcon, Trash2, CheckCircle2, Image as ImageIcon, Sparkles, AlertTriangle, ShieldAlert, MoreHorizontal, ChevronDown, CheckSquare, Square
} from 'lucide-react'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import { MultiSelectPopover, Option } from '@/components/shared/MultiSelectPopover'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'

interface MappedEntity { id: number; name?: string; title?: string }
interface TagItem { id: number; name: string }

interface Question {
  id: number
  external_id: string
  question_type: string
  difficulty: string
  status: string
  quality_score: number | null
  is_pyq: boolean
  is_exemplar: boolean
  has_svg?: boolean
  has_image?: boolean
  has_context_media?: boolean
  has_visual?: boolean
  primary_subject: string | null
  primary_chapter: string | null
  default_language: string
  preview_text: string
  tags?: TagItem[]
  mapped_exams?: MappedEntity[]
  mapped_syllabi?: MappedEntity[]
  mapped_tests?: MappedEntity[]
  mapped_standards?: MappedEntity[]
}

interface Subject { id: number; name: string }
interface Chapter { id: number; name: string; subject_id: number }
interface Section { id: number; name: string; chapter_id: number }
interface Exam { id: number; name: string; year: number | null }
interface Syllabus { id: number; name: string }
interface Test { id: number; title: string }

interface Filters {
  statuses: string[]
  difficulties: string[]
  question_types: string[]
  tags?: TagItem[]
  subjects: Subject[]
  chapters: Chapter[]
  sections: Section[]
  exams: Exam[]
  syllabi: Syllabus[]
  tests: Test[]
  media_types?: { id: string; name: string }[]
}

interface Meta extends PaginationMeta {}
interface Props {
  questions: Question[]
  total_filtered_count?: number
  status_counts?: Record<string, number>
  meta: Meta
  filters: Filters
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted/70 text-foreground border border-border',
  pending_review: 'bg-primary/15 text-primary border border-primary/30',
  approved: 'bg-secondary/15 text-secondary border border-secondary/30',
  published: 'bg-secondary/15 text-secondary border border-secondary/30',
  rejected: 'bg-destructive/15 text-destructive border border-destructive/30',
  needs_revision: 'bg-destructive/15 text-destructive border border-destructive/30',
  archived: 'bg-muted text-muted-foreground border border-border',
}

interface QuestionContextBadgesProps {
  mappedExams?: MappedEntity[]
  mappedSyllabi?: MappedEntity[]
  mappedTests?: MappedEntity[]
  mappedStandards?: MappedEntity[]
  returnUrl: string
  isLastRow?: boolean
  isFirstRow?: boolean
}

function QuestionContextBadges({
  mappedExams = [],
  mappedSyllabi = [],
  mappedTests = [],
  mappedStandards = [],
  returnUrl,
}: QuestionContextBadgesProps) {
  const [activeTab, setActiveTab] = useState<'exams' | 'syllabi' | 'tests' | 'standards' | null>(null)

  const cleanReturnUrl = returnUrl ? returnUrl.replace(/^https?:\/\/[^\/]+/, '') : '/admin/questions'

  const activeItems =
    activeTab === 'exams'
      ? { title: 'Exams', linkPrefix: '/admin/exams', list: mappedExams }
      : activeTab === 'syllabi'
      ? { title: 'Syllabi', linkPrefix: '/admin/syllabi', list: mappedSyllabi }
      : activeTab === 'tests'
      ? { title: 'Tests', linkPrefix: '/admin/tests', list: mappedTests }
      : activeTab === 'standards'
      ? { title: 'Standards', linkPrefix: '/admin/standards', list: mappedStandards }
      : null

  const open = Boolean(activeItems && activeItems.list.length > 0)

  return (
    <Popover open={open} onOpenChange={(isOpen) => { if (!isOpen) setActiveTab(null) }}>
      <div className="relative inline-flex flex-wrap items-center gap-1.5">
        {mappedExams.length > 0 && (
          <PopoverTrigger asChild>
            <Badge
              variant="outline"
              onClick={() => setActiveTab((prev) => (prev === 'exams' ? null : 'exams'))}
              className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
                activeTab === 'exams' ? 'bg-primary/15 text-primary border-primary/30 font-medium' : 'bg-background/50 hover:bg-accent'
              }`}
            >
              <GraduationCap className="h-3 w-3 text-primary" />
              <span>{mappedExams.length} Exams</span>
            </Badge>
          </PopoverTrigger>
        )}

        {mappedSyllabi.length > 0 && (
          <PopoverTrigger asChild>
            <Badge
              variant="outline"
              onClick={() => setActiveTab((prev) => (prev === 'syllabi' ? null : 'syllabi'))}
              className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
                activeTab === 'syllabi' ? 'bg-secondary/15 text-secondary border-secondary/30 font-medium' : 'bg-background/50 hover:bg-accent'
              }`}
            >
              <BookOpen className="h-3 w-3 text-secondary" />
              <span>{mappedSyllabi.length} Syllabi</span>
            </Badge>
          </PopoverTrigger>
        )}

        {mappedTests.length > 0 && (
          <PopoverTrigger asChild>
            <Badge
              variant="outline"
              onClick={() => setActiveTab((prev) => (prev === 'tests' ? null : 'tests'))}
              className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
                activeTab === 'tests' ? 'bg-tertiary/15 text-tertiary border-tertiary/30 font-medium' : 'bg-background/50 hover:bg-accent'
              }`}
            >
              <FileText className="h-3 w-3 text-tertiary" />
              <span>{mappedTests.length} Tests</span>
            </Badge>
          </PopoverTrigger>
        )}

        {mappedStandards.length > 0 && (
          <PopoverTrigger asChild>
            <Badge
              variant="outline"
              onClick={() => setActiveTab((prev) => (prev === 'standards' ? null : 'standards'))}
              className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
                activeTab === 'standards' ? 'bg-primary/15 text-primary border-primary/30 font-medium' : 'bg-background/50 hover:bg-accent'
              }`}
            >
              <Layers className="h-3 w-3 text-primary" />
              <span>{mappedStandards.length} Standards</span>
            </Badge>
          </PopoverTrigger>
        )}
      </div>

      {activeItems && activeItems.list.length > 0 && (
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-60 rounded-xl border-border bg-popover text-popover-foreground shadow-glass-md z-50 p-2.5 text-xs space-y-1.5"
        >
          <div className="font-semibold text-[11px] text-muted-foreground pb-1.5 border-b border-border flex justify-between items-center">
            <span className="font-semibold text-foreground">Mapped {activeItems.title} ({activeItems.list.length})</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-foreground rounded"
              onClick={() => setActiveTab(null)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5 pt-0.5">
            {activeItems.list.map((item) => (
              <Link
                key={item.id}
                href={`${activeItems.linkPrefix}/${item.id}?return_to=${encodeURIComponent(cleanReturnUrl)}`}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent hover:text-accent-foreground text-primary font-medium text-xs truncate transition-colors"
              >
                <span className="truncate">{item.name || item.title || `#${item.id}`}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ml-2" />
              </Link>
            ))}
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}

export default function QuestionsIndex({
  questions,
  total_filtered_count,
  status_counts,
  meta,
  filters,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [importBatchId, setImportBatchId] = useState<number | null>(null)
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  // Clear selections when question set changes
  useEffect(() => {
    setSelectedIds([])
  }, [questions])

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(questions.map(q => q.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id])
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id))
    }
  }

  const handleBulkPublish = (status = 'published') => {
    if (selectedIds.length === 0) return
    if (!confirm(`Are you sure you want to publish ${selectedIds.length} selected question(s)?`)) return
    router.post('/admin/questions/bulk_publish', { question_ids: selectedIds, status }, {
      onSuccess: () => setSelectedIds([])
    })
  }

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return
    if (!confirm(`Are you sure you want to permanently delete ${selectedIds.length} selected question(s)?`)) return
    router.delete('/admin/questions/bulk_delete', {
      data: { question_ids: selectedIds },
      onSuccess: () => setSelectedIds([])
    })
  }

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  const executeDeleteAllFiltered = () => {
    if (deleteConfirmInput.trim() !== 'DELETE') return
    setIsDeleting(true)

    const sp = new URLSearchParams(window.location.search)
    const filterParams: Record<string, any> = {}
    sp.forEach((value, key) => {
      if (filterParams[key]) {
        if (Array.isArray(filterParams[key])) {
          filterParams[key].push(value)
        } else {
          filterParams[key] = [filterParams[key], value]
        }
      } else {
        filterParams[key] = value
      }
    })

    router.delete('/admin/questions/delete_all_filtered', {
      data: filterParams,
      onFinish: () => {
        setIsDeleting(false)
        setDeleteDialogOpen(false)
        setDeleteConfirmInput('')
        setSelectedIds([])
      }
    })
  }

  const handlePublishAllDrafts = () => {
    const isOnlyDraftFilter = selectedStatuses.length === 1 && selectedStatuses[0] === 'draft'
    const targetCount = isOnlyDraftFilter
      ? (total_filtered_count || meta?.total_count || questions.length)
      : (status_counts?.draft || 0)

    if (targetCount === 0) {
      alert('There are no draft questions matching to publish.')
      return
    }

    if (!confirm(`Are you sure you want to publish all ${targetCount} draft question(s)? They will immediately become live for tests and student practice.`)) {
      return
    }

    setIsPublishing(true)
    const sp = new URLSearchParams(window.location.search)
    const filterParams: Record<string, any> = {}
    sp.forEach((value, key) => {
      if (filterParams[key]) {
        if (Array.isArray(filterParams[key])) {
          filterParams[key].push(value)
        } else {
          filterParams[key] = [filterParams[key], value]
        }
      } else {
        filterParams[key] = value
      }
    })

    router.post('/admin/questions/publish_all_filtered', {
      ...filterParams,
      only_drafts: 'true'
    }, {
      onFinish: () => {
        setIsPublishing(false)
        setSelectedIds([])
      }
    })
  }

  const handlePublishAllFiltered = () => {
    const total = total_filtered_count || meta?.total_count || questions.length
    if (!confirm(`Are you sure you want to publish ALL ${total} questions currently matching the active filters?`)) return

    setIsPublishing(true)
    const sp = new URLSearchParams(window.location.search)
    const filterParams: Record<string, any> = {}
    sp.forEach((value, key) => {
      if (filterParams[key]) {
        if (Array.isArray(filterParams[key])) {
          filterParams[key].push(value)
        } else {
          filterParams[key] = [filterParams[key], value]
        }
      } else {
        filterParams[key] = value
      }
    })

    router.post('/admin/questions/publish_all_filtered', filterParams, {
      onFinish: () => {
        setIsPublishing(false)
        setSelectedIds([])
      }
    })
  }


  const handleSingleDelete = (id: number, extId: string) => {
    if (!confirm(`Are you sure you want to delete question #${extId || id}?`)) return
    router.delete(`/admin/questions/${id}`)
  }

  // Helper to robustly read array params from URL search params
  const getArrayParam = (key: string): string[] => {
    if (typeof window === 'undefined') return []
    const sp = new URLSearchParams(window.location.search)
    const val1 = sp.getAll(`${key}[]`)
    const val2 = sp.getAll(key)
    const val3 = sp.get(key) ? sp.get(key)!.split(',') : []
    return Array.from(new Set([...val1, ...val2, ...val3])).filter(Boolean)
  }

  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const [searchText, setSearchText] = useState(searchParams.get('search') || '')
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)

  const selectedStatuses = getArrayParam('statuses').concat(searchParams.get('status') ? [searchParams.get('status')!] : []).filter(s => s !== 'all')
  const selectedDifficulties = getArrayParam('difficulties').concat(searchParams.get('difficulty') ? [searchParams.get('difficulty')!] : []).filter(d => d !== 'all')
  const selectedQuestionTypes = getArrayParam('question_types')
  const selectedTagNames = getArrayParam('tags').concat(searchParams.get('tag') ? [searchParams.get('tag')!] : [])

  const selectedSubjectIds = getArrayParam('subject_ids').concat(searchParams.get('subject_id') ? [searchParams.get('subject_id')!] : []).map(Number).filter(Boolean)
  const selectedChapterIds = getArrayParam('chapter_ids').map(Number).filter(Boolean)
  const selectedSectionIds = getArrayParam('section_ids').map(Number).filter(Boolean)

  const selectedExamIds = getArrayParam('exam_ids').map(Number).filter(Boolean)
  const selectedSyllabusIds = getArrayParam('syllabus_ids').map(Number).filter(Boolean)
  const selectedTestIds = getArrayParam('test_ids').map(Number).filter(Boolean)

  const selectedMediaTypes = getArrayParam('media_types')
    .concat(getArrayParam('media'))
    .concat(searchParams.get('media') ? [searchParams.get('media')!] : [])
    .filter(m => m !== 'all')

  const isPyq = searchParams.get('is_pyq') === 'true'
  const isExemplar = searchParams.get('is_exemplar') === 'true'

  // Cascading Hierarchy: Filter available Chapters by selected Subjects
  const availableChapters = selectedSubjectIds.length > 0
    ? filters.chapters.filter(c => selectedSubjectIds.includes(c.subject_id))
    : filters.chapters

  // Cascading Hierarchy: Filter available Sections by selected Chapters or Subjects
  const availableSections = selectedChapterIds.length > 0
    ? filters.sections.filter(s => selectedChapterIds.includes(s.chapter_id))
    : selectedSubjectIds.length > 0
    ? filters.sections.filter(s => {
        const parentChap = filters.chapters.find(c => c.id === s.chapter_id)
        return parentChap && selectedSubjectIds.includes(parentChap.subject_id)
      })
    : filters.sections

  const updateFilters = (overrideParams: Record<string, any>) => {
    const currentPerPage = searchParams.get('per_page') || searchParams.get('per') || (meta?.per_page ? String(meta.per_page) : undefined)
    const baseParams: Record<string, any> = {
      search: searchText.trim() || undefined,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      difficulties: selectedDifficulties.length > 0 ? selectedDifficulties : undefined,
      question_types: selectedQuestionTypes.length > 0 ? selectedQuestionTypes : undefined,
      tags: selectedTagNames.length > 0 ? selectedTagNames : undefined,
      subject_ids: selectedSubjectIds.length > 0 ? selectedSubjectIds : undefined,
      chapter_ids: selectedChapterIds.length > 0 ? selectedChapterIds : undefined,
      section_ids: selectedSectionIds.length > 0 ? selectedSectionIds : undefined,
      exam_ids: selectedExamIds.length > 0 ? selectedExamIds : undefined,
      syllabus_ids: selectedSyllabusIds.length > 0 ? selectedSyllabusIds : undefined,
      test_ids: selectedTestIds.length > 0 ? selectedTestIds : undefined,
      media: selectedMediaTypes.length > 0 ? selectedMediaTypes : undefined,
      is_pyq: isPyq ? 'true' : undefined,
      is_exemplar: isExemplar ? 'true' : undefined,
      per_page: currentPerPage,
      ...overrideParams,
    }

    const cleanParams: Record<string, any> = {}
    Object.keys(baseParams).forEach(k => {
      if (baseParams[k] !== undefined && baseParams[k] !== null) {
        cleanParams[k] = baseParams[k]
      }
    })

    router.get('/admin/questions', cleanParams, {
      preserveState: true,
      replace: true
    })
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateFilters({ search: searchText.trim() || undefined })
  }

  const handleClearAll = () => {
    setSearchText('')
    router.get('/admin/questions', {}, { preserveState: true, replace: true })
  }

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    setImporting(true)
    const form = new FormData()
    form.append('file', file)
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    try {
      const res = await fetch('/admin/questions/bulk_import', {
        method: 'POST',
        headers: { 'X-CSRF-Token': token },
        body: form
      })
      const data = await res.json()
      if (data.import_batch_id) {
        setImportBatchId(data.import_batch_id)
        setTimeout(() => router.reload(), 2000)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setImporting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  // Calculate active filter count
  const activeFiltersCount =
    selectedStatuses.length +
    selectedDifficulties.length +
    selectedQuestionTypes.length +
    selectedTagNames.length +
    selectedSubjectIds.length +
    selectedChapterIds.length +
    selectedSectionIds.length +
    selectedExamIds.length +
    selectedSyllabusIds.length +
    selectedTestIds.length +
    selectedMediaTypes.length +
    (isPyq ? 1 : 0) +
    (isExemplar ? 1 : 0) +
    (searchText.trim() ? 1 : 0)

  // Map options for MultiSelectPopovers
  const statusOptions: Option[] = (filters.statuses || []).map(s => ({ id: s, label: s.replace('_', ' ') }))
  const difficultyOptions: Option[] = (filters.difficulties || []).map(d => ({ id: d, label: d }))
  const questionTypeOptions: Option[] = (filters.question_types || []).map(t => ({ id: t, label: t.replace('_', ' ') }))
  const tagOptions: Option[] = (filters.tags || []).map(t => ({ id: t.name, label: t.name }))

  const subjectOptions: Option[] = (filters.subjects || []).map(s => ({ id: s.id, label: s.name }))
  const chapterOptions: Option[] = availableChapters.map(c => {
    const parentSub = filters.subjects.find(s => s.id === c.subject_id)
    return { id: c.id, label: c.name, sublabel: parentSub ? parentSub.name : undefined }
  })
  const sectionOptions: Option[] = availableSections.map(s => {
    const parentChap = filters.chapters.find(c => c.id === s.chapter_id)
    return { id: s.id, label: s.name, sublabel: parentChap ? parentChap.name : undefined }
  })

  const examOptions: Option[] = (filters.exams || []).map(e => ({ id: e.id, label: e.name, sublabel: e.year ? String(e.year) : undefined }))
  const syllabusOptions: Option[] = (filters.syllabi || []).map(sy => ({ id: sy.id, label: sy.name }))
  const testOptions: Option[] = (filters.tests || []).map(t => ({ id: t.id, label: t.title }))
  const mediaOptions: Option[] = (filters.media_types && filters.media_types.length > 0)
    ? filters.media_types.map(m => ({ id: m.id, label: m.name }))
    : [
        { id: 'any', label: 'Any Visual (Image / SVG / Diagram)' },
        { id: 'svg', label: 'SVG / Interactive Diagram' },
        { id: 'image', label: 'Figure Image' },
        { id: 'context_media', label: 'Context Media / Chart' },
      ]

  return (
    <AppShell>
      <div className="space-y-4 lg:space-y-6">
        {/* Mobile Header Bar (lg:hidden) */}
        <div className="lg:hidden space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold font-serif tracking-tight text-foreground">Question Bank</h1>
              <p className="text-xs text-muted-foreground">Browse & manage questions</p>
            </div>

            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
                className="h-8 px-2.5 text-xs font-medium border-border"
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>

          {/* Mobile Status Metrics Strip (Horizontal swipe bar) */}
          {status_counts && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 -mx-4 px-4 sm:mx-0 sm:px-0">
              {([
                { key: 'all', label: 'All', count: status_counts['all'] ?? 0 },
                { key: 'published', label: 'Published', count: status_counts['published'] ?? 0 },
                { key: 'draft', label: 'Draft', count: status_counts['draft'] ?? 0 },
                { key: 'needs_revision', label: 'Needs Revision', count: status_counts['needs_revision'] ?? 0 },
                { key: 'approved', label: 'Approved', count: status_counts['approved'] ?? 0 },
                { key: 'rejected', label: 'Rejected', count: status_counts['rejected'] ?? 0 },
              ]).map((item) => {
                const isSelected = item.key === 'all'
                  ? selectedStatuses.length === 0
                  : selectedStatuses.includes(item.key)

                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      if (item.key === 'all') {
                        updateFilters({ statuses: [], status: undefined })
                      } else {
                        const nextStatuses = selectedStatuses.includes(item.key)
                          ? selectedStatuses.filter(s => s !== item.key)
                          : [item.key]
                        updateFilters({ statuses: nextStatuses, status: undefined })
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0 transition-all cursor-pointer border ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary font-semibold shadow-xs'
                        : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <span>{item.label}</span>
                    <span
                      className={`inline-flex items-center justify-center min-w-[18px] px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                        isSelected
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-muted text-foreground/80'
                      }`}
                    >
                      {item.count}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Mobile Search & Filter Toolbar */}
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearchSubmit} className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Search text, ID, tags..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-9 pr-8 h-9 text-xs rounded-xl bg-card border-border"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => { setSearchText(''); updateFilters({ search: undefined }) }}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </form>

            <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant={activeFiltersCount > 0 ? 'default' : 'outline'}
                  size="sm"
                  className="h-9 px-3 gap-1.5 font-medium shrink-0 rounded-xl"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  <span className="text-xs">Filters</span>
                  {activeFiltersCount > 0 && (
                    <Badge className="px-1.5 py-0 text-[10px] bg-background text-foreground font-bold">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-5 bg-card border-border space-y-4">
                <SheetHeader className="pb-2 border-b border-border flex flex-row items-center justify-between">
                  <SheetTitle className="text-base font-bold flex items-center gap-2">
                    <Filter className="h-4 w-4 text-primary" /> Filter Questions
                  </SheetTitle>
                  {activeFiltersCount > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { handleClearAll(); setMobileFilterOpen(false) }}
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    >
                      Reset All
                    </Button>
                  )}
                </SheetHeader>

                {/* Filter Sections inside Mobile Sheet */}
                <div className="space-y-4 text-xs">
                  {/* Subject, Chapter, Section */}
                  <div className="space-y-2">
                    <label className="font-semibold text-foreground flex items-center gap-1.5">
                      <BookOpen className="h-3.5 w-3.5 text-primary" /> Subject & Topic
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      <MultiSelectPopover
                        title="Select Subject"
                        options={subjectOptions}
                        selectedValues={selectedSubjectIds}
                        onChange={(selected) => updateFilters({ subject_ids: selected, subject_id: undefined })}
                        className="w-full justify-between"
                      />
                      {availableChapters.length > 0 && (
                        <MultiSelectPopover
                          title="Select Chapter"
                          options={chapterOptions}
                          selectedValues={selectedChapterIds}
                          onChange={(selected) => updateFilters({ chapter_ids: selected })}
                          className="w-full justify-between"
                        />
                      )}
                      {availableSections.length > 0 && (
                        <MultiSelectPopover
                          title="Select Section"
                          options={sectionOptions}
                          selectedValues={selectedSectionIds}
                          onChange={(selected) => updateFilters({ section_ids: selected })}
                          className="w-full justify-between"
                        />
                      )}
                    </div>
                  </div>

                  {/* Status, Difficulty & Type */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="font-semibold text-foreground flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5 text-primary" /> Type & Difficulty
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <MultiSelectPopover
                        title="Question Type"
                        options={questionTypeOptions}
                        selectedValues={selectedQuestionTypes}
                        onChange={(selected) => updateFilters({ question_types: selected })}
                        className="w-full justify-between"
                      />
                      <MultiSelectPopover
                        title="Difficulty"
                        options={difficultyOptions}
                        selectedValues={selectedDifficulties}
                        onChange={(selected) => updateFilters({ difficulties: selected, difficulty: undefined })}
                        className="w-full justify-between"
                      />
                    </div>
                  </div>

                  {/* Exam, Syllabus, Tests */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="font-semibold text-foreground flex items-center gap-1.5">
                      <GraduationCap className="h-3.5 w-3.5 text-primary" /> Exam & Test Context
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <MultiSelectPopover
                        title="Select Exam"
                        options={examOptions}
                        selectedValues={selectedExamIds}
                        onChange={(selected) => updateFilters({ exam_ids: selected })}
                        className="w-full justify-between"
                      />
                      <MultiSelectPopover
                        title="Select Syllabus"
                        options={syllabusOptions}
                        selectedValues={selectedSyllabusIds}
                        onChange={(selected) => updateFilters({ syllabus_ids: selected })}
                        className="w-full justify-between"
                      />
                    </div>
                  </div>

                  {/* Media & Tags */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="font-semibold text-foreground flex items-center gap-1.5">
                      <TagIcon className="h-3.5 w-3.5 text-primary" /> Tags & Media
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <MultiSelectPopover
                        title="Tags"
                        options={tagOptions}
                        selectedValues={selectedTagNames}
                        onChange={(selected) => updateFilters({ tags: selected.map(String), tag: undefined })}
                        className="w-full justify-between"
                      />
                      <MultiSelectPopover
                        title="Visual / Media"
                        options={mediaOptions}
                        selectedValues={selectedMediaTypes}
                        onChange={(selected) => updateFilters({ media: selected.map(String) })}
                        className="w-full justify-between"
                      />
                    </div>
                  </div>

                  {/* Source Flags */}
                  <div className="pt-2 border-t border-border flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPyq}
                        onChange={(e) => updateFilters({ is_pyq: e.target.checked ? 'true' : undefined })}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                      />
                      PYQ Only
                    </label>
                    <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isExemplar}
                        onChange={(e) => updateFilters({ is_exemplar: e.target.checked ? 'true' : undefined })}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                      />
                      Exemplar Only
                    </label>
                  </div>
                </div>

                <div className="pt-3 border-t border-border">
                  <Button
                    type="button"
                    className="w-full bg-primary text-primary-foreground font-semibold"
                    onClick={() => setMobileFilterOpen(false)}
                  >
                    Apply Filters ({total_filtered_count ?? meta?.total_count ?? questions.length} Results)
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Desktop Header Bar (hidden lg:flex) */}
        <div className="hidden lg:flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-serif text-foreground">Question Bank</h1>
            <p className="text-muted-foreground mt-1 text-sm">Browse, review, and bulk import questions</p>
          </div>

          {/* Desktop Status Metrics Strip */}
          {status_counts && (
            <div className="flex flex-wrap items-center gap-1.5 p-1.5 rounded-xl bg-card border border-border/70 shadow-xs">
              {([
                { key: 'all', label: 'All', count: status_counts['all'] ?? 0, color: 'text-foreground hover:bg-muted' },
                { key: 'published', label: 'Published', count: status_counts['published'] ?? 0, color: 'text-secondary hover:bg-secondary/10' },
                { key: 'draft', label: 'Draft', count: status_counts['draft'] ?? 0, color: 'text-muted-foreground hover:bg-muted' },
                { key: 'needs_revision', label: 'Needs Revision', count: status_counts['needs_revision'] ?? 0, color: 'text-destructive hover:bg-destructive/10' },
                { key: 'approved', label: 'Approved', count: status_counts['approved'] ?? 0, color: 'text-primary hover:bg-primary/10' },
                { key: 'rejected', label: 'Rejected', count: status_counts['rejected'] ?? 0, color: 'text-destructive hover:bg-destructive/10' },
              ]).map((item) => {
                const isSelected = item.key === 'all'
                  ? selectedStatuses.length === 0
                  : selectedStatuses.includes(item.key)

                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      if (item.key === 'all') {
                        updateFilters({ statuses: [], status: undefined })
                      } else {
                        const nextStatuses = selectedStatuses.includes(item.key)
                          ? selectedStatuses.filter(s => s !== item.key)
                          : [item.key]
                        updateFilters({ statuses: nextStatuses, status: undefined })
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${item.color} ${
                      isSelected
                        ? 'bg-muted font-bold ring-1.5 ring-primary/40 shadow-2xs'
                        : 'opacity-85'
                    }`}
                    title={`Filter by ${item.label}`}
                  >
                    <span>{item.label}</span>
                    <span
                      className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                        isSelected
                          ? 'bg-background text-foreground shadow-2xs'
                          : 'bg-muted/70 text-foreground/80'
                      }`}
                    >
                      {item.count}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
            <Button variant="default" onClick={() => fileRef.current?.click()} disabled={importing}>
              <Upload className="mr-2 h-4 w-4" />
              {importing ? 'Importing…' : 'Bulk Import JSON'}
            </Button>
          </div>
        </div>

        {importBatchId && (
          <div className="rounded-md border border-primary/30 bg-primary/10 p-4 text-sm flex items-center justify-between">
            <div>
              <span>Import processing in background. Batch ID: <strong>#{importBatchId}</strong>.</span>
            </div>
            <Button size="sm" variant="outline" onClick={() => router.reload()}>
              Refresh Table
            </Button>
          </div>
        )}

        {/* Desktop Primary Quick Filters Bar (hidden lg:block) */}
        <Card className="shadow-xs hidden lg:block border-border bg-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Search Form & Primary Quick Popovers */}
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <form onSubmit={handleSearchSubmit} className="relative min-w-[220px] flex-1 max-w-xs">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Search ID, text, tag:source..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </form>

                <MultiSelectPopover
                  title="Tags"
                  icon={<TagIcon className="h-3.5 w-3.5 text-primary" />}
                  options={tagOptions}
                  selectedValues={selectedTagNames}
                  onChange={(selected) => updateFilters({ tags: selected.map(String), tag: undefined })}
                />

                <MultiSelectPopover
                  title="Status"
                  options={statusOptions}
                  selectedValues={selectedStatuses}
                  onChange={(selected) => updateFilters({ statuses: selected, status: undefined })}
                />

                <MultiSelectPopover
                  title="Difficulty"
                  options={difficultyOptions}
                  selectedValues={selectedDifficulties}
                  onChange={(selected) => updateFilters({ difficulties: selected, difficulty: undefined })}
                />

                <MultiSelectPopover
                  title="Subject"
                  icon={<BookOpen className="h-3.5 w-3.5 text-primary" />}
                  options={subjectOptions}
                  selectedValues={selectedSubjectIds}
                  onChange={(selected) => updateFilters({ subject_ids: selected, subject_id: undefined })}
                />

                {availableChapters.length > 0 && (
                  <MultiSelectPopover
                    title="Chapter"
                    icon={<FolderTree className="h-3.5 w-3.5 text-primary" />}
                    options={chapterOptions}
                    selectedValues={selectedChapterIds}
                    onChange={(selected) => updateFilters({ chapter_ids: selected })}
                  />
                )}

                <MultiSelectPopover
                  title="Visual / Media"
                  icon={<ImageIcon className="h-3.5 w-3.5 text-secondary" />}
                  options={mediaOptions}
                  selectedValues={selectedMediaTypes}
                  onChange={(selected) => updateFilters({ media: selected.map(String) })}
                />
              </div>

              {/* All Filters Toggle Button */}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={showDrawer ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setShowDrawer(!showDrawer)}
                  className="h-9 gap-2 font-medium"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span>All Filters</span>
                  {activeFiltersCount > 0 && (
                    <Badge className="px-1.5 py-0 text-[10px] bg-primary text-primary-foreground font-semibold">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>

            {/* Expandable Context-Aware Filter Panel */}
            {showDrawer && (
              <div className="pt-3 mt-3 border-t border-border grid gap-4 md:grid-cols-2 lg:grid-cols-5 animate-in fade-in-50">
                {/* Taxonomy Cascading Row */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Layers className="h-3.5 w-3.5 text-primary" /> Section Filter
                  </label>
                  <MultiSelectPopover
                    title="Select Section"
                    options={sectionOptions}
                    selectedValues={selectedSectionIds}
                    onChange={(selected) => updateFilters({ section_ids: selected })}
                    className="w-full"
                  />
                </div>

                {/* Exam & Syllabus */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <GraduationCap className="h-3.5 w-3.5 text-primary" /> Exam & Syllabus
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <MultiSelectPopover
                      title="Exam"
                      options={examOptions}
                      selectedValues={selectedExamIds}
                      onChange={(selected) => updateFilters({ exam_ids: selected })}
                    />
                    <MultiSelectPopover
                      title="Syllabus"
                      options={syllabusOptions}
                      selectedValues={selectedSyllabusIds}
                      onChange={(selected) => updateFilters({ syllabus_ids: selected })}
                    />
                  </div>
                </div>

                {/* Question Type & Tests */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5 text-primary" /> Type & Test
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <MultiSelectPopover
                      title="Question Type"
                      options={questionTypeOptions}
                      selectedValues={selectedQuestionTypes}
                      onChange={(selected) => updateFilters({ question_types: selected })}
                    />
                    <MultiSelectPopover
                      title="Test"
                      options={testOptions}
                      selectedValues={selectedTestIds}
                      onChange={(selected) => updateFilters({ test_ids: selected })}
                    />
                  </div>
                </div>

                {/* Media & Diagrams */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <ImageIcon className="h-3.5 w-3.5 text-secondary" /> Media & Diagrams
                  </label>
                  <MultiSelectPopover
                    title="Filter by Media"
                    icon={<ImageIcon className="h-3.5 w-3.5 text-secondary" />}
                    options={mediaOptions}
                    selectedValues={selectedMediaTypes}
                    onChange={(selected) => updateFilters({ media: selected.map(String) })}
                    className="w-full"
                  />
                </div>

                {/* Source Flags */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">Source Flags</label>
                  <div className="flex items-center gap-4 h-9 pt-1">
                    <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPyq}
                        onChange={(e) => updateFilters({ is_pyq: e.target.checked ? 'true' : undefined })}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                      />
                      PYQ Only
                    </label>
                    <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isExemplar}
                        onChange={(e) => updateFilters({ is_exemplar: e.target.checked ? 'true' : undefined })}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                      />
                      Exemplar Only
                    </label>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Filters Row (Responsive) */}
        {activeFiltersCount > 0 && (
          <div className="p-2.5 rounded-xl bg-muted/40 border border-border flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-[11px] font-semibold text-muted-foreground mr-1 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Active ({activeFiltersCount}):
            </span>

            {searchText.trim() && (
              <Badge variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                Search: &quot;{searchText}&quot;
                <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => { setSearchText(''); updateFilters({ search: undefined }) }} />
              </Badge>
            )}

            {selectedMediaTypes.map(m => {
              const opt = mediaOptions.find(o => o.id === m)
              return (
                <Badge key={m} variant="secondary" className="text-[11px] gap-1 font-normal bg-secondary/15 text-secondary border-secondary/30">
                  <ImageIcon className="h-3 w-3 text-secondary" /> {opt ? opt.label : m}
                  <X className="h-3 w-3 cursor-pointer hover:text-secondary/70" onClick={() => updateFilters({ media: selectedMediaTypes.filter(v => v !== m) })} />
                </Badge>
              )
            })}

            {selectedStatuses.map(s => (
              <Badge key={s} variant="secondary" className="text-[11px] gap-1 font-normal capitalize bg-card border-border">
                Status: {s.replace('_', ' ')}
                <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ statuses: selectedStatuses.filter(v => v !== s), status: undefined })} />
              </Badge>
            ))}

            {selectedTagNames.map(tag => (
              <Badge key={tag} variant="secondary" className="text-[11px] gap-1 font-normal bg-primary/10 text-primary border-primary/20">
                <TagIcon className="h-3 w-3 text-primary" /> {tag}
                <X className="h-3 w-3 cursor-pointer hover:text-primary/70" onClick={() => updateFilters({ tags: selectedTagNames.filter(v => v !== tag), tag: undefined })} />
              </Badge>
            ))}

            {selectedDifficulties.map(d => (
              <Badge key={d} variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                Difficulty: {d}
                <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ difficulties: selectedDifficulties.filter(v => v !== d), difficulty: undefined })} />
              </Badge>
            ))}

            {selectedSubjectIds.map(id => {
              const sub = filters.subjects.find(s => s.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-primary/10 text-primary border-primary/20">
                  Subject: {sub ? sub.name : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-primary/70" onClick={() => updateFilters({ subject_ids: selectedSubjectIds.filter(v => v !== id), subject_id: undefined })} />
                </Badge>
              )
            })}

            {selectedChapterIds.map(id => {
              const chap = filters.chapters.find(c => c.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-primary/10 text-primary border-primary/20">
                  Chapter: {chap ? chap.name : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-primary/70" onClick={() => updateFilters({ chapter_ids: selectedChapterIds.filter(v => v !== id) })} />
                </Badge>
              )
            })}

            {selectedSectionIds.map(id => {
              const sec = filters.sections.find(s => s.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-primary/10 text-primary border-primary/20">
                  Section: {sec ? sec.name : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-primary/70" onClick={() => updateFilters({ section_ids: selectedSectionIds.filter(v => v !== id) })} />
                </Badge>
              )
            })}

            {selectedExamIds.map(id => {
              const ex = filters.exams.find(e => e.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                  Exam: {ex ? ex.name : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ exam_ids: selectedExamIds.filter(v => v !== id) })} />
                </Badge>
              )
            })}

            {selectedSyllabusIds.map(id => {
              const sy = filters.syllabi.find(s => s.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                  Syllabus: {sy ? sy.name : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ syllabus_ids: selectedSyllabusIds.filter(v => v !== id) })} />
                </Badge>
              )
            })}

            {selectedTestIds.map(id => {
              const t = filters.tests.find(ts => ts.id === id)
              return (
                <Badge key={id} variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                  Test: {t ? t.title : id}
                  <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ test_ids: selectedTestIds.filter(v => v !== id) })} />
                </Badge>
              )
            })}

            {isPyq && (
              <Badge variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                PYQ Only
                <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ is_pyq: undefined })} />
              </Badge>
            )}

            {isExemplar && (
              <Badge variant="secondary" className="text-[11px] gap-1 font-normal bg-card border-border">
                Exemplar Only
                <X className="h-3 w-3 cursor-pointer hover:text-foreground" onClick={() => updateFilters({ is_exemplar: undefined })} />
              </Badge>
            )}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-5 px-1.5 text-[11px] text-muted-foreground hover:text-foreground gap-1"
            >
              <RotateCcw className="h-2.5 w-2.5" /> Clear All
            </Button>
          </div>
        )}

        {/* Bulk Actions / Filter Summary Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 bg-muted/40 p-2.5 sm:p-3 rounded-xl border border-border">
          <div className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={questions.length > 0 && selectedIds.length === questions.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded border-input text-primary focus:ring-primary h-4 w-4"
              aria-label="Select all questions on this page"
            />
            <span className="font-medium text-foreground">
              {questions.length} of {total_filtered_count ?? meta?.total_count ?? questions.length} questions
            </span>
            {selectedIds.length > 0 && (
              <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-primary text-primary-foreground font-bold">
                {selectedIds.length} selected
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {selectedIds.length > 0 ? (
              <>
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 sm:h-8 gap-1 text-xs bg-primary hover:bg-primary/90"
                  onClick={() => handleBulkPublish('published')}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Publish ({selectedIds.length})</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 sm:h-8 gap-1 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Delete ({selectedIds.length})</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 sm:h-8 text-xs text-muted-foreground"
                  onClick={() => setSelectedIds([])}
                >
                  Deselect
                </Button>
              </>
            ) : (
              (total_filtered_count ?? meta?.total_count ?? questions.length) > 0 && (
                <div className="flex items-center gap-1.5">
                  {/* Primary Positive Action: Publish All Drafts */}
                  {(selectedStatuses.includes('draft') || (status_counts?.draft || 0) > 0) && (
                    <Button
                      size="sm"
                      variant="default"
                      disabled={isPublishing}
                      className="h-7 sm:h-8 gap-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs font-medium"
                      onClick={handlePublishAllDrafts}
                      title="Publish draft questions matching current filter"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>
                        {selectedStatuses.length === 1 && selectedStatuses[0] === 'draft'
                          ? `Publish All (${total_filtered_count ?? meta?.total_count ?? questions.length})`
                          : `Publish Drafts (${status_counts?.draft || 0})`}
                      </span>
                    </Button>
                  )}

                  {/* Overflow / Secondary Actions Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 sm:h-8 text-xs text-muted-foreground hover:text-foreground gap-1 border-border bg-card"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5 opacity-70" />
                        <span>Bulk Tools</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 text-xs rounded-xl bg-card border-border shadow-glass-md z-50">
                      <DropdownMenuItem
                        onClick={handlePublishAllFiltered}
                        disabled={isPublishing}
                        className="gap-2 cursor-pointer text-foreground focus:bg-primary/10 focus:text-primary"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                        <span>Publish All Matching ({total_filtered_count ?? meta?.total_count ?? questions.length})</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border/60" />
                      <DropdownMenuItem
                        onClick={() => {
                          setDeleteConfirmInput('')
                          setDeleteDialogOpen(true)
                        }}
                        className="gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                        <span>Delete All Filtered...</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )
            )}
          </div>
        </div>

        {/* Top Pagination */}
        <Pagination
          meta={meta}
          onPageChange={(page) => updateFilters({ page })}
          onPerPageChange={(per_page) => updateFilters({ page: 1, per_page })}
          itemLabel="questions"
        />

        {/* Mobile Question Cards Feed (lg:hidden) */}
        <div className="lg:hidden space-y-3">
          {questions.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border">
              <p className="text-xs text-muted-foreground">No questions match the selected filters. Try clearing some filters.</p>
            </Card>
          ) : (
            questions.map((q) => {
              const isSelected = selectedIds.includes(q.id)
              return (
                <Card
                  key={q.id}
                  className={`p-3.5 bg-card border-border rounded-xl shadow-xs transition-all space-y-2.5 ${
                    isSelected ? 'ring-1.5 ring-primary bg-primary/5' : ''
                  }`}
                >
                  {/* Top Row: Checkbox, Type, Difficulty & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectOne(q.id, e.target.checked)}
                        className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                        aria-label={`Select question`}
                      />
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 uppercase bg-muted/50 border-border font-medium">
                        {q.question_type.replace('_', ' ')}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {q.difficulty}
                      </Badge>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[q.status] || 'bg-muted'}`}>
                        {q.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Question Teaser / Preview */}
                  <Link href={`/admin/questions/${q.id}`} className="block group">
                    <KatexRenderer
                      content={q.preview_text || 'No translation text'}
                      className="text-xs text-foreground line-clamp-2 leading-relaxed font-serif group-hover:text-primary transition-colors"
                    />
                  </Link>

                  {/* Taxonomy & Media Badges */}
                  <div className="flex flex-wrap items-center gap-1 pt-0.5">
                    {q.primary_subject && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-muted/60 text-foreground border-border font-medium">
                        <BookOpen className="mr-1 h-2.5 w-2.5 text-primary" />
                        {q.primary_subject} {q.primary_chapter ? `› ${q.primary_chapter}` : ''}
                      </Badge>
                    )}

                    {q.is_pyq && <Badge variant="outline" className="text-[10px] px-1 py-0">PYQ</Badge>}
                    {q.is_exemplar && <Badge variant="outline" className="text-[10px] px-1 py-0">Exemplar</Badge>}

                    {q.has_svg && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 gap-1 bg-secondary/15 text-secondary border-secondary/30 font-semibold cursor-pointer"
                        onClick={() => updateFilters({ media: ['svg'] })}
                        title="Contains interactive SVG diagram"
                      >
                        <Sparkles className="h-2.5 w-2.5" /> SVG
                      </Badge>
                    )}

                    {q.has_image && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 gap-1 bg-primary/10 text-primary border-primary/25 font-semibold cursor-pointer"
                        onClick={() => updateFilters({ media: ['image'] })}
                        title="Contains figure image"
                      >
                        <ImageIcon className="h-2.5 w-2.5" /> Image
                      </Badge>
                    )}

                    {q.has_context_media && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 gap-1 bg-tertiary/10 text-tertiary border-tertiary/30 font-semibold cursor-pointer"
                        onClick={() => updateFilters({ media: ['context_media'] })}
                      >
                        <Layers className="h-2.5 w-2.5" /> Context
                      </Badge>
                    )}

                    {q.tags && q.tags.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 cursor-pointer font-mono border-border text-muted-foreground hover:text-primary hover:border-primary/40 bg-background/50 hover:bg-muted transition-colors gap-1"
                          >
                            <TagIcon className="h-2.5 w-2.5 text-primary" />
                            <span>{q.tags.length} Tags</span>
                            <ChevronDown className="h-2.5 w-2.5 opacity-60" />
                          </Badge>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-64 p-3 bg-card border-border rounded-xl shadow-glass-md z-50">
                          <div className="text-xs font-semibold text-foreground pb-1.5 mb-2 border-b border-border flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              <TagIcon className="h-3 w-3 text-primary" /> Question Tags ({q.tags.length})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto no-scrollbar">
                            {q.tags.map((t) => (
                              <Badge
                                key={t.id}
                                variant="outline"
                                onClick={() => {
                                  if (!selectedTagNames.includes(t.name)) {
                                    updateFilters({ tags: [...selectedTagNames, t.name] })
                                  }
                                }}
                                className="text-[10px] px-1.5 py-0.5 cursor-pointer font-mono border-border/70 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
                                title={`Filter by tag: ${t.name}`}
                              >
                                #{t.name}
                              </Badge>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}

                    <QuestionContextBadges
                      mappedExams={q.mapped_exams}
                      mappedSyllabi={q.mapped_syllabi}
                      mappedTests={q.mapped_tests}
                      mappedStandards={q.mapped_standards}
                      returnUrl={typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/admin/questions'}
                    />
                  </div>

                  {/* Card Action Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/60 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground uppercase font-mono">{q.default_language}</span>
                      <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[120px]" title={q.external_id || String(q.id)}>
                        #{q.external_id || q.id}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" asChild className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
                        <Link href={`/admin/questions/${q.id}`}>
                          <ExternalLink className="mr-1 h-3 w-3" /> Details
                        </Link>
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                        onClick={() => handleSingleDelete(q.id, q.external_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              )
            })
          )}
        </div>

        {/* Desktop Questions Table (hidden lg:block) */}
        <Card className="overflow-visible hidden lg:block border-border bg-card">
          <CardContent className="p-0 overflow-visible">
            <Table className="overflow-visible">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={questions.length > 0 && selectedIds.length === questions.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                      aria-label="Select all questions on this page"
                    />
                  </TableHead>
                  <TableHead>Preview</TableHead>
                  <TableHead className="w-28">Type</TableHead>
                  <TableHead className="w-24">Difficulty</TableHead>
                  <TableHead>Subject / Chapter</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-16">Lang</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {questions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No questions match the selected filters. Try clearing some filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  questions.map((q, idx) => {
                    const isSelected = selectedIds.includes(q.id)
                    return (
                      <TableRow key={q.id} className={isSelected ? 'bg-muted/30' : ''}>
                        <TableCell className="w-10">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectOne(q.id, e.target.checked)}
                            className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                            aria-label={`Select question #${q.external_id || q.id}`}
                          />
                        </TableCell>
                        <TableCell className="max-w-md space-y-1">
                          <Link
                            href={`/admin/questions/${q.id}`}
                            className="block group hover:opacity-85 transition-opacity"
                          >
                            <KatexRenderer content={q.preview_text || 'No translation text'} className="text-sm line-clamp-2 text-foreground group-hover:text-primary transition-colors" />
                          </Link>
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            <Link
                              href={`/admin/questions/${q.id}`}
                              className="text-[11px] font-mono font-semibold text-primary hover:underline mr-1 inline-flex items-center"
                              title={`Open question #${q.external_id || q.id}`}
                            >
                              #{q.external_id || q.id}
                            </Link>
                            {q.is_pyq && <Badge variant="outline" className="text-[10px] px-1 py-0">PYQ</Badge>}
                            {q.is_exemplar && <Badge variant="outline" className="text-[10px] px-1 py-0">Exemplar</Badge>}

                            {q.has_svg && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 gap-1 bg-secondary/15 text-secondary border-secondary/30 font-semibold cursor-pointer"
                                onClick={() => updateFilters({ media: ['svg'] })}
                                title="Contains interactive SVG diagram in solution (click to filter)"
                              >
                                <Sparkles className="h-2.5 w-2.5" /> SVG Diagram
                              </Badge>
                            )}

                            {q.has_image && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 gap-1 bg-primary/10 text-primary border-primary/25 font-semibold cursor-pointer"
                                onClick={() => updateFilters({ media: ['image'] })}
                                title="Contains figure image (click to filter)"
                              >
                                <ImageIcon className="h-2.5 w-2.5" /> Image
                              </Badge>
                            )}

                            {q.has_context_media && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 gap-1 bg-tertiary/10 text-tertiary border-tertiary/30 font-semibold cursor-pointer"
                                onClick={() => updateFilters({ media: ['context_media'] })}
                                title="Contains context media or chart (click to filter)"
                              >
                                <Layers className="h-2.5 w-2.5" /> Context Media
                              </Badge>
                            )}

                            {q.tags && q.tags.map((t) => (
                              <Badge
                                key={t.id}
                                variant="outline"
                                onClick={() => {
                                  if (!selectedTagNames.includes(t.name)) {
                                    updateFilters({ tags: [...selectedTagNames, t.name] })
                                  }
                                }}
                                className="text-[10px] px-1.5 py-0 cursor-pointer font-mono border-border/70 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
                                title={`Filter by tag: ${t.name}`}
                              >
                                #{t.name}
                              </Badge>
                            ))}

                            <QuestionContextBadges
                              mappedExams={q.mapped_exams}
                              mappedSyllabi={q.mapped_syllabi}
                              mappedTests={q.mapped_tests}
                              mappedStandards={q.mapped_standards}
                              returnUrl={typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/admin/questions'}
                              isFirstRow={idx === 0}
                              isLastRow={idx > 0 && idx >= questions.length - 2}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-xs uppercase">{q.question_type.replace('_', ' ')}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{q.difficulty}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{q.primary_subject ?? '—'}</div>
                          <div className="text-muted-foreground text-xs">{q.primary_chapter ?? '—'}</div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || 'bg-muted'}`}>
                            {q.status.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs uppercase">{q.default_language}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" asChild title="View Question Details" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                              <Link href={`/admin/questions/${q.id}`}>
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleSingleDelete(q.id, q.external_id)}
                              title="Delete Question"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Bottom Pagination */}
        <Pagination
          meta={meta}
          onPageChange={(page) => updateFilters({ page })}
          onPerPageChange={(per_page) => updateFilters({ page: 1, per_page })}
          itemLabel="questions"
        />

        {/* Protected Modal for Delete All Filtered */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md bg-card border-border text-foreground rounded-2xl shadow-glass-lg z-50">
            <DialogHeader>
              <div className="flex items-center gap-2 text-destructive font-bold text-base">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                <DialogTitle>Permanently Delete Filtered Questions</DialogTitle>
              </div>
              <DialogDescription className="text-xs text-muted-foreground pt-2 leading-relaxed">
                You are about to permanently delete <strong>{total_filtered_count ?? meta?.total_count ?? questions.length}</strong> questions matching your active filters.
                This action is <strong>irreversible</strong> and will delete all associated answers, options, and translations.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-3">
              <label className="text-xs font-semibold text-foreground">
                To confirm deletion, type <span className="font-mono font-bold text-destructive">DELETE</span> below:
              </label>
              <Input
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder="Type DELETE"
                className="h-9 text-xs font-mono bg-background border-border"
                autoFocus
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialogOpen(false)}
                className="text-xs rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={deleteConfirmInput.trim() !== 'DELETE' || isDeleting}
                onClick={executeDeleteAllFiltered}
                className="text-xs font-bold gap-1.5 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {isDeleting ? 'Deleting...' : `Permanently Delete ${total_filtered_count ?? meta?.total_count ?? questions.length} Questions`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  )
}
